import ReactMarkdown from 'react-markdown'
import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import Decimal from 'decimal.js'

import InputRangeSlider from 'components/InputRangeSlider'

import { getSharesRequiredQuestionLabels, getSharesHoldProcessExplanation } from 'models/General/selectors'
import { getBalance, getPrimaryEmailAddress, getPrimaryPhoneNumber } from 'models/ProfileInfo/selectors'

import { STRING } from 'resources'
import { APP_URLS } from 'resources/urls'

import PrimaryLink from 'units/PrimaryLink'
import formatCurrency from 'utils/format-currency'
import formatPoints from 'utils/format-points'
import getDecimalValue from 'utils/get-decimal-value'

import MissingPrimaryIdsModal from './missingPrimaryIdsModal'
import AutoRedeemSharesSection from './AutoRedeemSharesSection'
import {
  Form,
  SectionContainer,
  SectionSeparator,
  SectionBoldText,
  SectionText,
  SharesRequirementsNote,
  CancelButton,
  RedemptionProcessNote,
  ItalicizedRequirements,
  RedemptionProcessNoteContainer,
  RedemptionSelectionButton,
  RedemptionProcessNoteWarning,
} from './styles'

const SharesAmountSelector = ({
  reward,
  selectedNumberOfShares,
  setSelectedNumberOfShares,
  onRequest,
  isRequesting,
  errorMessage,
}) => {
  const balance = useSelector(getBalance)
  const primaryEmailAddress = useSelector(getPrimaryEmailAddress)
  const primaryPhoneNumber = useSelector(getPrimaryPhoneNumber)
  const sharesRequiredQuestionLabels = useSelector(getSharesRequiredQuestionLabels)
  const sharesHoldProcessExplanation = useSelector(getSharesHoldProcessExplanation)

  const [isMissingPrimaryIdsModalOpen, setIsMissingPrimaryIdsModalOpen] = useState(false)
  const [hasViewedMissingPrimaryIdsModal, setHasViewedMissingPrimaryIdsModal] = useState(false)

  const maxValue = getDecimalValue(reward.redemptionValues[reward.redemptionValues.length - 1] || 0)
  const maxAmount = Decimal.min(balance, maxValue)
  const minShares = reward.minShares || 1
  const maxShares = Decimal.max(maxAmount.div(Decimal(reward.sharePrice)).floor(), minShares).toString()

  const missingPrimaryId = !primaryEmailAddress || !primaryPhoneNumber
  const lowFunds = Decimal(balance).lt(Decimal(reward.sharePrice).mul(reward.minShares))

  const handleRequestAmountChange = useCallback(
    (value) => {
      if (!value || value >= 0) {
        setSelectedNumberOfShares(value)
      }
    },
    [setSelectedNumberOfShares],
  )

  const handleContinuePress = useCallback(() => {
    if (missingPrimaryId) {
      setIsMissingPrimaryIdsModalOpen(true)
    } else {
      onRequest()
    }
  }, [missingPrimaryId, onRequest])

  const handleModalToggle = useCallback(() => {
    setIsMissingPrimaryIdsModalOpen(!isMissingPrimaryIdsModalOpen)
    setHasViewedMissingPrimaryIdsModal(true)
  }, [isMissingPrimaryIdsModalOpen])

  return (
    <>
      <MissingPrimaryIdsModal isOpen={isMissingPrimaryIdsModalOpen} toggle={handleModalToggle} />
      {lowFunds && (
        <RedemptionProcessNoteWarning>{STRING.wallet.redemptions.shares.lowfunds}</RedemptionProcessNoteWarning>
      )}
      {hasViewedMissingPrimaryIdsModal && (
        <RedemptionProcessNoteWarning>{STRING.wallet.redemptions.shares.missingIdError}</RedemptionProcessNoteWarning>
      )}
      <Form onSubmit={onRequest}>
        <SectionContainer>
          <SectionBoldText>{STRING.wallet.redemptions.shares.numberOfShares(selectedNumberOfShares)}</SectionBoldText>
        </SectionContainer>
        <InputRangeSlider
          type="range"
          min={minShares}
          max={maxShares}
          step={1}
          size="2"
          onChange={(event) => handleRequestAmountChange(event.target.value)}
          value={selectedNumberOfShares}
          disabled={lowFunds}
        />
        <SectionSeparator />
        <SectionContainer>
          <SectionBoldText>{STRING.wallet.redemptions.shares.sharePrice}</SectionBoldText>
          <SectionText>{formatCurrency(reward.sharePrice)}</SectionText>
        </SectionContainer>
        <SectionContainer>
          <SectionBoldText>{STRING.wallet.redemptions.shares.ptsAmount}</SectionBoldText>
          <SectionText>{formatPoints(selectedNumberOfShares * reward.sharePrice)}</SectionText>
        </SectionContainer>
        <SectionContainer>
          <SharesRequirementsNote>
            {STRING.wallet.redemptions.shares.requirements}
            <br />
            <ItalicizedRequirements>{sharesRequiredQuestionLabels.join(', ')}</ItalicizedRequirements>
          </SharesRequirementsNote>
        </SectionContainer>
        {!!errorMessage && <RedemptionProcessNoteWarning>{errorMessage}</RedemptionProcessNoteWarning>}
        <AutoRedeemSharesSection />
        <RedemptionSelectionButton onPress={handleContinuePress} disabled={lowFunds} isLoading={isRequesting}>
          {STRING.wallet.redemptions.shares.continue}
        </RedemptionSelectionButton>
        <CancelButton to={APP_URLS.STORE} tag={PrimaryLink}>
          {STRING.wallet.redemptions.shares.chooseAnotherReward}
        </CancelButton>
        <RedemptionProcessNoteContainer>
          <RedemptionProcessNote>
            <ReactMarkdown>{sharesHoldProcessExplanation}</ReactMarkdown>
          </RedemptionProcessNote>
        </RedemptionProcessNoteContainer>
      </Form>
    </>
  )
}

SharesAmountSelector.propTypes = {
  reward: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    categoryId: PropTypes.string.isRequired,
    isTop: PropTypes.bool.isRequired,
    imageUrl: PropTypes.string.isRequired,
    redemptionValues: PropTypes.arrayOf(PropTypes.string).isRequired,
    currentPrice: PropTypes.string,
    dayChange: PropTypes.string,
    fees: PropTypes.string,
    sharePrice: PropTypes.string.isRequired,
    minShares: PropTypes.number.isRequired,
  }).isRequired,
  selectedNumberOfShares: PropTypes.string.isRequired,
  setSelectedNumberOfShares: PropTypes.func.isRequired,
  isRequesting: PropTypes.bool.isRequired,
  onRequest: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
}

export default SharesAmountSelector
